import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Paginate } from 'seedly-component-library';
import DropdownSelect from 'seedly-component-library/dropdown-select';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'reducer';
import { getRewardsRequest } from 'entity/reward/rewardRedux';
import { RewardSort } from 'entity/reward/rewardTypes';
import useWindowWidth from 'utils/hooks/useWindowWidth';
import useIsMobile from 'utils/hooks/useIsMobile';
import { checkIsRedeemable } from 'entity/reward/rewardUtils';
import { useIsEnrolledUser } from 'entity/user/userHooks';
import mixpanel, { trackedEvents } from 'utils/mixpanel';
import clsx from 'clsx';
import RewardEmptyState from './RewardEmptyState';
import RewardItem from '../RewardItem';
import useFilterList from './useFilterList';
import useSortList from './useSortList';
import useMyRewardList from './useMyRewardList';

const getRewardSortByParam = (rewardSort: RewardSort) => {
  if (rewardSort === 'featured') return { sort_by_featured: true };
  if (rewardSort === 'latest') return { sort_by_recency: true };
  if (rewardSort === 'creditAsc') return { sort_by_credits: 'asc' };
  if (rewardSort === 'creditDesc') return { sort_by_credits: 'desc' };
  return { sort_by_featured: true };
};

const getMyRewardFilterByParam = (filter: string) => {
  if (filter === 'all') return {};
  if (filter === 'current') return { expired: false };
  if (filter === 'past') return { expired: true };

  return {};
};

const RewardList = props => {
  const { rewardTabFilter } = props;

  const [category, setCategory] = useState<string>('all');
  const [sort, setSortBy] = useState<RewardSort>('featured');
  const [myRewardFilter, setMyRewardFilter] = useState('all');
  const [onlyCanRedeem, setOnlyCanRedeem] = useState(false);
  const [page, setPage] = useState(1);

  const rewardIds = useSelector((state: RootState) => state.reward.ids);
  const rewardById = useSelector((state: RootState) => state.reward.byId);
  const profile = useSelector((state: RootState) => state.profile.user);
  const pagination = useSelector((state: RootState) => state.reward.pagination);

  const categoryList = useFilterList();
  const sortList = useSortList();
  const rewardFilterList = useMyRewardList();
  const windowWidth = useWindowWidth();
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const isEnrolled = useIsEnrolledUser();

  const buttonWidth = useMemo(() => {
    // for desktop
    if (!isMobile) return '240px';

    // for mobile, 32px is the left + right padding and gap between.
    // divide by 2 for each button
    const availableWidth = (windowWidth - 48) / 2;
    return `${availableWidth}px`;
  }, [windowWidth, isMobile]);

  // set to page 1 once change reward tab
  useEffect(() => {
    setPage(1);
  }, [rewardTabFilter, category, sort]);

  useEffect(() => {
    if (rewardTabFilter === 'my-reward') {
      setCategory('all');
      setSortBy('featured');
    }
  }, [rewardTabFilter]);

  // this is for browse reward
  useEffect(() => {
    if (rewardTabFilter === 'browse-reward') {
      let tier: number[];
      if (category === 'tier1') {
        tier = [1];
      }

      if (category === 'tier1&2') {
        tier = [1, 2];
      }

      dispatch(
        getRewardsRequest({
          ...getRewardSortByParam(sort),
          ...(category !== 'all' && !tier && { category }),
          ...(tier && { tier }),
          ...(onlyCanRedeem && { redeemable_only: true }),
          per: 18,
          page,
        }),
      );
    }
  }, [category, sort, rewardTabFilter, page, onlyCanRedeem]);

  // this is for my reward
  useEffect(() => {
    if (rewardTabFilter === 'my-reward') {
      dispatch(
        getRewardsRequest({
          ...getMyRewardFilterByParam(myRewardFilter),
          ...(rewardTabFilter === 'my-reward' && { personal: true }),
          per: 18,
          page,
        }),
      );
    }
  }, [myRewardFilter, rewardTabFilter, page, dispatch]);

  const rewardList = rewardIds.map(id => {
    return rewardById[id];
  });

  return (
    <div className="flex flex-col px-4 lg:px-0 w-full gap-6">
      <div className="flex flex-col gap-2">
        {rewardTabFilter === 'browse-reward' && (
          <div className="flex gap-2 items-center">
            <div style={{ width: buttonWidth }}>
              <DropdownSelect
                variant="white"
                items={categoryList}
                defaultSelectedItem={categoryList[0]}
                handleSelectedItemchange={item => {
                  setCategory(item.selectedItem.value);
                }}
                label="Filter by"
                titleFormat={title => {
                  if (isMobile && title.length >= 15)
                    return `${title.substring(0, 15)}...`;
                  return title;
                }}
                isBlock
              />
            </div>
            <div style={{ width: buttonWidth }}>
              <DropdownSelect
                variant="white"
                items={sortList}
                defaultSelectedItem={sortList[0]}
                handleSelectedItemchange={item => {
                  setSortBy(item.selectedItem.value);
                }}
                label="Sort by"
                titleFormat={title => {
                  if (isMobile && title.length >= 15)
                    return `${title.substring(0, 15)}...`;
                  return title;
                }}
                isBlock
              />
            </div>
          </div>
        )}
        <div
          className={clsx(rewardTabFilter === 'my-reward' ? 'block' : 'hidden')}
          style={{ width: buttonWidth }}
        >
          <DropdownSelect
            variant="white"
            items={rewardFilterList}
            defaultSelectedItem={rewardFilterList[0]}
            handleSelectedItemchange={item => {
              setMyRewardFilter(item.selectedItem.value);
            }}
            isBlock
          />
        </div>
        {rewardTabFilter === 'browse-reward' && isEnrolled && (
          <Checkbox
            name="redeem-rewards"
            checked={onlyCanRedeem}
            onChange={e => {
              setOnlyCanRedeem(e.target.checked);
              mixpanel.track(trackedEvents.RewardsCollection, {
                param: 'show_only_rewards_i_can_redeem',
              });
            }}
            labelSpace="xs"
          >
            Show only rewards I can redeem
          </Checkbox>
        )}
      </div>
      {rewardList.length > 0 ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 w-full">
          {rewardList
            .map(reward => {
              const canRedeem = checkIsRedeemable(profile, reward);

              // claimed rewards
              if (reward.claims?.length > 0) {
                return reward.claims
                  .map(claim => {
                    if (claim.used_at && myRewardFilter === 'current')
                      return null;

                    if (!claim.used_at && myRewardFilter === 'past')
                      return null;

                    return (
                      <RewardItem
                        canRedeem={canRedeem}
                        reward={reward}
                        key={`c${claim.id}`}
                        claim={claim}
                      />
                    );
                  })
                  .filter(component => !!component);
              }

              return (
                <RewardItem
                  canRedeem={canRedeem}
                  reward={reward}
                  key={`r${reward.id}`}
                  claim={null}
                />
              );
            })
            .flat()}
        </div>
      ) : (
        <RewardEmptyState rewardTabFilter={rewardTabFilter} />
      )}
      <div className="flex flex-col w-full items-center">
        {pagination.totalPage > 1 && rewardList.length > 0 && (
          <Paginate
            pageCount={pagination.totalPage}
            forcePage={pagination.currentPage - 1}
            onPageChange={data => {
              setPage(data.selected + 1);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default RewardList;
